import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import { DEFAULT_COIN_ICON } from '../../utils/helpers';
import { API_BASE_URL } from '../../utils/apiConfig';
import './HeaderSearch.css';

function HeaderSearch({ targetPath = '/price' }) {
  const { t } = useTranslation();
  const { localizedPath } = useLanguage();
  const navigate = useNavigate();

  const [isExpanded, setIsExpanded] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const closingTimer = useRef(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [availableCoins, setAvailableCoins] = useState([]);
  const [coinsLoading, setCoinsLoading] = useState(true);
  const [symbolsCache, setSymbolsCache] = useState({});

  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Load available coins from API.
  // `cache: 'no-cache'` forces the browser to revalidate via If-None-Match on
  // every navigation. Without this, Cloudflare was serving the response with
  // `Cache-Control: max-age=14400` (4 h) which kept showing customers a stale
  // coin list — including BTC/ETH/SOL appearing missing from the top of the
  // dropdown long after backend rankings had shifted.
  useEffect(() => {
    const loadAvailableCoins = async () => {
      try {
        setCoinsLoading(true);
        const response = await fetch(`${API_BASE_URL}/api/available-coins`, { cache: 'no-cache' });
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();

        if (data.success) {
          setAvailableCoins(data.coins);
        }
      } catch (error) {
        console.error('[HeaderSearch] Error loading available coins:', error);
        setAvailableCoins([]);
      } finally {
        setCoinsLoading(false);
      }
    };
    loadAvailableCoins();
  }, []);

  // Load symbols cache for icons
  useEffect(() => {
    const loadSymbolsCache = async () => {
      try {
        const apiResponse = await fetch(`${API_BASE_URL}/api/icons/all`);
        if (apiResponse.ok) {
          const data = await apiResponse.json();
          setSymbolsCache(data);
          return;
        }
      } catch (error) {
        console.warn('[HeaderSearch] API failed, trying JSON fallback');
      }

      try {
        const response = await fetch(`${API_BASE_URL}/mapped_symbols.json`);
        if (response.ok) {
          const data = await response.json();
          setSymbolsCache(data);
        }
      } catch (error) {
        console.error('[HeaderSearch] Failed to load symbols cache');
      }
    };
    loadSymbolsCache();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        collapse();
      }
    };

    if (isExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isExpanded]);

  // When expanded: focus input + show dropdown after expand animation
  useEffect(() => {
    if (isExpanded) {
      inputRef.current?.focus();
      const timer = setTimeout(() => setShowDropdown(true), 250);
      return () => clearTimeout(timer);
    } else {
      setShowDropdown(false);
    }
  }, [isExpanded]);

  // Reset scroll to the top whenever the dropdown is freshly shown so the user
  // always sees BTC / ETH / SOL first instead of a mid-list position carried
  // over from a previous open.
  useEffect(() => {
    if (showDropdown && dropdownRef.current) {
      dropdownRef.current.scrollTop = 0;
    }
  }, [showDropdown]);

  // Filter coins based on search term
  const filteredCoins = useMemo(() => {
    if (!searchTerm.trim()) return availableCoins.slice(0, 40);
    const term = searchTerm.toUpperCase();
    return availableCoins.filter(coin => coin.includes(term)).slice(0, 40);
  }, [availableCoins, searchTerm]);

  // Get coin icon URL
  const getCoinIconUrl = (coin) => {
    const normalizedSymbol = `${coin.toUpperCase()}USDT`;
    if (symbolsCache[normalizedSymbol]) {
      return symbolsCache[normalizedSymbol];
    }
    return DEFAULT_COIN_ICON;
  };

  const collapse = () => {
    setShowDropdown(false);
    setIsExpanded(false);
    setIsClosing(true);
    setSearchTerm('');
    clearTimeout(closingTimer.current);
    closingTimer.current = setTimeout(() => setIsClosing(false), 350);
  };

  // Handle coin selection — navigate to the configured target page for that coin
  const handleSelectCoin = (coin) => {
    collapse();
    const base = targetPath.replace(/\/$/, '');
    navigate(localizedPath(`${base}/${coin}`));
  };

  // Handle key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      const term = searchTerm.trim().toUpperCase();
      if (availableCoins.includes(term)) {
        handleSelectCoin(term);
      } else if (filteredCoins.length > 0) {
        handleSelectCoin(filteredCoins[0]);
      }
    } else if (e.key === 'Escape') {
      collapse();
    }
  };

  // Expand on click (only when collapsed)
  const handleFieldClick = () => {
    if (!isExpanded) {
      setIsExpanded(true);
    }
  };

  return (
    <div className={`header-search ${isExpanded ? 'expanded' : ''} ${isClosing ? 'closing' : ''}`} ref={containerRef}>
      {/* Single element that morphs from button to input */}
      <div className="header-search-field" onClick={handleFieldClick}>
        <input
          ref={inputRef}
          type="text"
          className="header-search-input"
          placeholder={t('header.searchCoins', 'Search coins...')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={handleKeyPress}
          tabIndex={isExpanded ? 0 : -1}
        />
        <svg className="header-search-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
          <path d="M18.031 16.6168L22.3137 20.8995L20.8995 22.3137L16.6168 18.031C15.0769 19.263 13.124 20 11 20C6.032 20 2 15.968 2 11C2 6.032 6.032 2 11 2C15.968 2 20 6.032 20 11C20 13.124 19.263 15.0769 18.031 16.6168ZM16.0247 15.8748C17.2475 14.6146 18 12.8956 18 11C18 7.1325 14.8675 4 11 4C7.1325 4 4 7.1325 4 11C4 14.8675 7.1325 18 11 18C12.8956 18 14.6146 17.2475 15.8748 16.0247L16.0247 15.8748Z"/>
        </svg>
      </div>

      {/* Dropdown — appears after field finishes expanding */}
      {showDropdown && (
        <div className="header-search-dropdown" ref={dropdownRef}>
          {coinsLoading ? (
            <div className="header-search-loading">
              <div className="header-search-spinner"></div>
              <span>{t('common.loading', 'Loading...')}</span>
            </div>
          ) : filteredCoins.length > 0 ? (
            filteredCoins.map(coin => (
              <div
                key={coin}
                className="header-search-item"
                onClick={() => handleSelectCoin(coin)}
              >
                <img
                  src={getCoinIconUrl(coin)}
                  alt={coin}
                  className="header-search-item-icon"
                  onError={(e) => { e.target.src = DEFAULT_COIN_ICON; }}
                />
                <span className="header-search-item-name">{coin}</span>
              </div>
            ))
          ) : (
            <div className="header-search-empty">
              {t('header.noCoinsFound', 'No coins found')}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default HeaderSearch;
